// #region Imports
import SaveIcon from "@mui/icons-material/Save";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import LoadingButton from "_shared/_views/loading/loading-button";
import PlaceAutocomplete from "_shared/addresses/_views/place-autocomplete";
import Address from "_shared/addresses/address";
import YearInput from "_shared/dates/years/_views/year-input";
import Year from "_shared/dates/years/year";
import Guid from "_shared/guids/guid";
import URLInput from "_shared/urls/url-input";
import axios from "axios";
import { CompanyProfileTypes, defaultProfileName } from "config";
import AvatarUpload from "marketplace/_views/avatars/avatar-upload";
import ProfileCategoriesSelect from "marketplace/_views/profiles/profile-categories-select";
import ProfileNameInput from "marketplace/_views/profiles/profile-name-input";
import {
  getCompanyAvatar,
  getCompanyById,
} from "marketplace/companies/_store/companies-redux-slice";
import { companiesApi } from "marketplace/companies/_api/company-api";
import Company from "marketplace/companies/company";
import CompanyProfileContactPersonInput from "marketplace/companies/profiles/_views/inputs/company-profile-contact-person-input";
import CompanyProfileNumEmployeesInput from "marketplace/companies/profiles/_views/inputs/company-profile-num-employees-input";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import IndividualSummary from "marketplace/individuals/profiles/summaries/individual-summary";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(() => ({
  display: "grid",
  gridTemplateRows: "repeat(4, auto)",
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    "& > *": {
      margin: 0,
      padding: theme.spacing(0, 0, 3, 0),
      width: "100%",
    },
    "&:not(last-child)": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "row",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(1, 0, 4, 0),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
  },
  columnFill: "balance",
  columnGap: theme.spacing(4),
  gridColumn: "1 / auto",
  gridRowStart: "2",
  paddingTop: theme.spacing(0.75),
}));
const AboutYou = styled("section")(({ theme }) => ({
  gridColumn: "1 / span 3",
  gridRowStart: "3",
  marginTop: theme.spacing(1),
}));
const AboutYouTextField = styled(TextField)(() => ({
  width: "100%",
}));
const AvatarNameContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
}));
const YearEstablishedInput = styled(YearInput)(({ theme }) => ({
  minWidth: theme.spacing(20),
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const NumEmployeesInput = styled(CompanyProfileNumEmployeesInput)(({ theme }) => ({
  minWidth: theme.spacing(20),
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const ContactPersonInput = styled(CompanyProfileContactPersonInput)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const CompanyWebsite = styled(URLInput)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const CompanyLocation = styled(PlaceAutocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  bottom: 0,
  gridColumn: "1",
  gridRowStart: "4",
  justifySelf: "flex-start",
  padding: theme.spacing(2, 0),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));
const CategoriesSelector = styled(ProfileCategoriesSelect)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    marginRight: 0,
  },
}));
// #endregion

// #region Component Props
export type EditCompanyProfileProps = {
  isVisible?: boolean;
  companyId?: Guid;
  profile?: CompanyProfile;
  onFormDirty?: (isDirty?: boolean) => void;
  onSave?: (company: Company) => void;
};
// #endregion

export default function EditCompanyProfile(props: Readonly<EditCompanyProfileProps>) {
  const { profile, onFormDirty, onSave } = props;

  // #region State
  const [saving, setSaving] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(props.isVisible ?? false);

  const [companyId, setCompanyId] = useState<Guid | undefined>(props.companyId);
  const [originalCompanyProfile, setOriginalCompanyProfile] = useState<CompanyProfile | undefined>(profile);
  const [updatedCompanyProfile, setUpdatedCompanyProfile] = useState<CompanyProfile | undefined>(profile);
  const [updatedAvatar, setUpdatedAvatar] = useState<File | undefined>();

  const [draftCompanyName, setDraftCompanyName] = useState<string | null>(null);
  const [formCompanyName, setFormCompanyName] = useState<string>(profile?.name ?? "");
  const [formContactPerson, setFormContactPerson] = useState<IndividualSummary | null>(null);
  const [formYearEstablished, setFormYearEstablished] = useState<string>(profile?.yearEstablished?.toString() ?? "");
  const [formNumberOfEmployees, setFormNumberOfEmployees] = useState<string>(
    profile?.numberOfEmployees?.toString() ?? "",
  );
  const [formWebsiteUrl, setFormWebsiteUrl] = useState<string>(profile?.website?.toString() ?? "");
  const [formLocation, setFormLocation] = useState<Address | null>(profile?.location ?? null);
  const [formCategories, setFormCategories] = useState<string[]>(profile?.categories ?? []);
  const [formDescription, setFormDescription] = useState<string>(profile?.description ?? "");
  const [formValidated, setFormValidated] = useState(false);
  const [profileHash, setProfileHash] = useState<string>("");

  // #region Hooks
  const session = useSession();

  // #region Selectors
  const company = useSelector(getCompanyById(companyId));
  const { isLoading, error: errorLoading } = companiesApi.useGetCompanyByIdQuery(
    { companyId: companyId?.value ?? "" },
    { skip: !companyId || !!company }
  );
  const existingAvatar = useSelector(getCompanyAvatar(companyId ?? null));
  companiesApi.useGetCompanyAvatarQuery(
    { companyId: companyId?.value ?? "" },
    { skip: !companyId || !company?.profile?.avatarId || !!existingAvatar }
  );

  // Mutations
  const [createCompanyMutation] = companiesApi.useCreateCompanyMutation();
  const [updateCompanyMutation] = companiesApi.useUpdateCompanyMutation();
  const [createCompanyAvatarMutation] = companiesApi.useCreateCompanyAvatarMutation();

  const locationServicesRef = useRef<boolean>(false);

  // #region Validation
  function validateProfile(profile: CompanyProfile) {
    if (!profile) return;
    const hasCategories: boolean = profile.categories?.length > 0;
    const hasName: boolean = Boolean(profile.name);
    const hasContactPerson: boolean = !!profile.contactUserId;
    setFormValidated(hasCategories && hasName && hasContactPerson);
  }

  const isDirty = (): boolean => {
    if (!updatedCompanyProfile) {
      onFormDirty?.(false);
      return false;
    }
    const hash = JSON.stringify(updatedCompanyProfile.toJSON());
    const isDirty = hash !== profileHash;
    onFormDirty?.(isDirty);
    return isDirty;
  };

  const canSave = (): boolean => !isLoading && !saving && isDirty() && formValidated;
  // #endregion

  // #region Lifecycle
  useEffect(() => {
    // Creating a new profile
    if (!companyId) {
      setUpdatedCompanyProfile(
        CompanyProfile.create({
          name: "New Profile",
          categories: [],
        }),
      );
      return;
    }

    // No company/profile
    if (!company?.profile) return;

    // Nothing changed
    if (profileHash === JSON.stringify(company.profile?.toJSON())) return;

    // Populate form with company profile data
    setOriginalCompanyProfile(company.profile);
    setUpdatedCompanyProfile(company.profile);
    setIsVisible(company.isVisible);
    setFormCompanyName(company.profile.name ?? "Unknown Company");
    setFormYearEstablished(company.profile.yearEstablished?.toString() ?? "");
    setFormNumberOfEmployees(company.profile.numberOfEmployees?.toString() ?? "");
    setFormWebsiteUrl(company.profile.website?.toString() ?? "");
    setFormLocation(company.profile.location ?? null);
    setFormCategories(company.profile.categories ?? []);
    setFormDescription(company.profile.description ?? "");

    validateProfile(company.profile);

    setProfileHash(JSON.stringify(company.profile?.toJSON()));
  }, [company, companyId, profileHash]);

  useEffect(() => {
    if (errorLoading) {
      enqueueSnackbar("Profile currently unavailable", { variant: "warning" });
    }
  }, [errorLoading]);
  // #endregion

  // #region Functions
  function constructCompany(): Company {
    if (!session.company?.legalEntityId) throw new Error("Company EntityId not found");
    if (!updatedCompanyProfile) throw new Error("Company profile not found");

    return new Company({
      id: companyId ?? Guid.generate(),
      legalEntityId: session.company?.legalEntityId,
      profile: updatedCompanyProfile,
      contactId: updatedCompanyProfile?.contactUserId,
      isVisible,
    });
  }

  async function handleSaveButtonClick() {
    if (companyId) await updateProfile();
    else await createProfile();
  }

  async function updateProfile() {
    if (!companyId || !originalCompanyProfile || !updatedCompanyProfile) return;

    try {
      setSaving(true);
      if (updatedAvatar) {
        await createCompanyAvatarMutation({ companyId: companyId.value, file: updatedAvatar }).unwrap();
      }
      const returnedCompanyJson = await updateCompanyMutation({
        companyId: companyId.value,
        isVisible,
        contactId: formContactPerson?.id,
        originalProfile: originalCompanyProfile,
        updatedProfile: updatedCompanyProfile,
      }).unwrap();
      const returnedCompany = Company.fromJSON(returnedCompanyJson);

      setOriginalCompanyProfile(returnedCompany.profile);
      setUpdatedCompanyProfile(returnedCompany.profile);

      enqueueSnackbar("Profile changes saved", { variant: "success" });
      onSave?.(returnedCompany);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to update profile", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function createProfile() {
    try {
      setSaving(true);
      const companyToCreate: Company = constructCompany();

      const returnedCompanyJson = await createCompanyMutation({
        company: companyToCreate,
      }).unwrap();

      const returnedCompany = Company.fromJSON(returnedCompanyJson);
      enqueueSnackbar("Company profile created", { variant: "success" });
      onSave?.(returnedCompany);

      setCompanyId(returnedCompany.id);
      setOriginalCompanyProfile(returnedCompany.profile);
      setUpdatedCompanyProfile(returnedCompany.profile);

      if (updatedAvatar) {
        await createAvatar(returnedCompany.id, updatedAvatar);
      }

      const hash = JSON.stringify(returnedCompanyJson);
      setProfileHash(hash);
    } catch (error) {
      const errorMessage = axios.isAxiosError(error) || error instanceof Error ? error.message : String(error);
      console.error(`There was a problem creating the company profile: ${errorMessage}`);
      enqueueSnackbar(`Problem while creating profile ${errorMessage}`, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function createAvatar(companyId: Guid, avatar: File) {
    if (!companyId || !avatar) return;

    try {
      await createCompanyAvatarMutation({
        companyId: companyId.value,
        file: avatar,
      }).unwrap();
    } catch (error) {
      console.error(error);
      throw new Error("Unable to create avatar");
    }
  }
  // #endregion

  // #region Render
  return (
    <MainContainer>
      <HeaderContainer>
        <AvatarNameContainer>
          <AvatarUpload
            accept="image/jpeg, image/png"
            avatar={existingAvatar ?? updatedAvatar}
            onUpload={(updatedAvatar) => setUpdatedAvatar(updatedAvatar)}
          />
          <ProfileNameInput
            name={formCompanyName}
            draftName={draftCompanyName ?? undefined}
            isLoading={isLoading}
            error={formCompanyName === defaultProfileName && companyId && !isLoading}
            onStartEditing={() => setDraftCompanyName(formCompanyName)}
            onDraftChange={(draft: string) => setDraftCompanyName(draft)}
            onCommit={(finalName: string) => {
              setFormCompanyName(finalName);
              if (!updatedCompanyProfile) return;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                name: finalName,
              });
              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
              setDraftCompanyName(null);
            }}
          />
        </AvatarNameContainer>
        <CategoriesSelector
          options={CompanyProfileTypes}
          categories={formCategories}
          onChange={async (updatedCategories: string[]) => {
            setFormCategories(updatedCategories);
            if (!updatedCompanyProfile) return;
            if (updatedCategories === updatedCompanyProfile.categories) return;
            const updatedProfile = new CompanyProfile({
              ...updatedCompanyProfile,
              categories: updatedCategories,
            });
            setUpdatedCompanyProfile(updatedProfile);
            validateProfile(updatedProfile);
          }}
        />
      </HeaderContainer>
      {!isLoading && (
        <ContentContainer>
          <ContactPersonInput
            required
            contactUserId={updatedCompanyProfile?.contactUserId}
            onChange={(value: IndividualSummary | null) => {
              setFormContactPerson(value);
              if (!updatedCompanyProfile || !value?.userId) return;
              if (value.userId.isEqualTo(updatedCompanyProfile.contactUserId)) return;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                contactUserId: value.userId,
              });
              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
            }}
          />
          <YearEstablishedInput
            label="Year Established"
            variant="outlined"
            year={Year.fromInput(formYearEstablished)}
            cannotBeInFuture
            onChange={(newYear?: Year) => {
              if (!newYear) return;
              setFormYearEstablished(newYear.string);
              if (!updatedCompanyProfile) return;
              if (newYear.value === updatedCompanyProfile.yearEstablished) return;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                yearEstablished: newYear.value,
              });
              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
            }}
          />
          <NumEmployeesInput
            numEmployees={formNumberOfEmployees}
            onChange={(employeeNumberValue: string) => {
              const trimmedValue = employeeNumberValue.trim();
              setFormNumberOfEmployees(trimmedValue);
              if (!updatedCompanyProfile) return;
              if (employeeNumberValue === updatedCompanyProfile.numberOfEmployees?.toString()) return;
              const numberOfEmployees = trimmedValue.length ? Number.parseInt(trimmedValue) : 0;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                numberOfEmployees: Math.max(numberOfEmployees, 0),
              });

              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
            }}
          />
          <CompanyWebsite
            variant="outlined"
            url={formWebsiteUrl?.toString()}
            onChange={(url: string, error?: string) => {
              setFormWebsiteUrl(url);
              if (error) setFormValidated(false);
              if (!updatedCompanyProfile || !url || error) return;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                website: url,
              });
              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
            }}
          />
          <CompanyLocation
            label="Location"
            variant="outlined"
            value={formLocation}
            locationServicesDisabledRef={locationServicesRef}
            onLocationServicesDisabledChange={(newValue) => {
              locationServicesRef.current = newValue;
            }}
            onSelected={(value: Address | null) => {
              setFormLocation(value);
              if (!updatedCompanyProfile || value?.isEqualTo(updatedCompanyProfile.location)) return;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                location: value ?? undefined,
              });
              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
            }}
          />
        </ContentContainer>
      )}
      {!isLoading && (
        <AboutYou>
          <Typography variant="h6">About Your Company</Typography>
          <AboutYouTextField
            id="about-you-textfield"
            value={formDescription}
            onChange={(event) => {
              setFormDescription(event.target.value);
              if (!updatedCompanyProfile) return;
              if (event.target.value === updatedCompanyProfile.description) return;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                description: event.target.value,
              });
              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
            }}
            multiline={true}
            minRows={4}
            placeholder="Additional info displayed on your profile"
            variant="outlined"
          />
        </AboutYou>
      )}
      <ButtonContainer>
        <LoadingButton
          id="saveButton"
          variant="contained"
          startIcon={<SaveIcon />}
          loading={saving}
          color="primary"
          disabled={!canSave()}
          onClick={handleSaveButtonClick}
        >
          {companyId ? "Save Changes" : "Create Profile"}
        </LoadingButton>
      </ButtonContainer>
    </MainContainer>
  );
}
// #endregion
